import React, { useMemo, forwardRef, useState, useEffect } from 'react';
import ReactDOMServer from 'react-dom/server';
import SimpleMDE, { SimpleMDEReactProps } from 'react-simplemde-editor';
import 'easymde/dist/easymde.min.css';
import ReactMarkdown from 'react-markdown';
import { getClassNames } from '../../helpers/classHelpers';
import { BaseUIType } from '../../models/UITypes';
import { withUIState } from '../../contexts/ui-state/withUIState';
import { isValidNumber } from '../../helpers/numberHelpers';
import style from './markdownEditor.module.scss';
import InputLabel from '../input-label/InputLabel';
import { useGetImageFullUrl, useUploadImage } from '../../features/dashboards/admin/hooks/useAdminService';
import EasyMDE from 'easymde';
import useLabels, { useInputValidationLabels } from '../../hooks/useLabels';
import { readableFileSize } from '../../helpers/fileHelpers';
import useRefLive from '../../hooks/useRefLive';
import { isNumber } from '../../helpers/typeHelpers';
import Alert from '../alert/Alert';

export type MarkdownEditorProps = BaseUIType & {
    label?: string;
    value?: string;
    onChange?: (value: string) => void;
    validationHandler?: (isInvalid: boolean) => void;
    maxHeight?: number;
    isDisabled?: boolean;
    placeholder?: string;
    className?: string;
    maxLength?: number;
    allowImageUpload?: boolean;
    hideStatusBar?: boolean;
};

const uploadButton = {
    name: "upload-image",
    action: EasyMDE.drawUploadedImage,
    className: "fa fa-file-image-o",
    title: "Upload Image",
};
const imageAccept = ["image/png", "image/jpg", "image/jpeg"];
const imageMaxSize = 1024 * 1024 * 2; //(2 MB),
const MarkdownEditor = forwardRef<HTMLDivElement, MarkdownEditorProps>(({
    label,
    value,
    onChange,
    validationHandler,
    maxHeight,
    isDisabled = false,
    placeholder,
    className,
    maxLength,
    allowImageUpload = true,
    hideStatusBar = false,
}, ref) => {
    const { mutateAsync: uploadImage } = useUploadImage();
    const { getFullImageUrl } = useGetImageFullUrl();
    const labels = useLabels();
    const toolbarItems = useMemo(() => [
        "bold",
        "italic",
        "heading",
        "|",
        "quote",
        "unordered-list",
        "ordered-list",
        "|",
        "link",
        ...(allowImageUpload ? ["image", uploadButton] : []),
        "|",
        "preview",
        "side-by-side",
        "fullscreen",
        "|",
        "guide"
    ], [allowImageUpload]);

    const customOptions = useMemo<EasyMDE.Options>(() => {
        return {
            spellChecker: false,
            ...(hideStatusBar ? { status: hideStatusBar } : {}),
            maxHeight: isValidNumber(maxHeight) ? `${maxHeight}px` : undefined,
            uploadImage: true,
            imageAccept: imageAccept.join(", "),
            imageMaxSize: imageMaxSize,
            toolbar: isDisabled ? [] : toolbarItems as EasyMDE.ToolbarIcon[],
            imageUploadFunction: async (file, onSuccess, onError) => {
                if (!allowImageUpload) {
                    onError("Image uploads are not allowed.");
                    return;
                }

                let error: string | undefined;

                if (!imageAccept.includes(file.type)) {
                    error = labels.invalidDocumentTypeMessage(imageAccept.map(x => "." + x.split('/')[1]));
                }
                if (file.size > imageMaxSize) {
                    const readableFileSizeLimit = readableFileSize(imageMaxSize);
                    error = labels.exceededDocSizeError(readableFileSizeLimit);
                }
                if (error) {
                    onError(error);
                    return;
                }

                try {
                    const { image_path } = await uploadImage({ file });
                    const defaultSizeQuery = '?width=300&mode=min';
                    const url = getFullImageUrl(image_path)! + defaultSizeQuery;
                    onSuccess(url);
                } catch (error) {
                    // Client error message is displayed by the global mutation error handler.
                }
            },
            previewRender: (plainText: string) => {
                return ReactDOMServer.renderToString(
                    <div className={getClassNames([style.preview])}>
                        <ReactMarkdown>{plainText}</ReactMarkdown>
                    </div>
                );
            },
        };
    }, [maxHeight, isDisabled, toolbarItems, hideStatusBar, allowImageUpload, labels, uploadImage, getFullImageUrl]);


    const [sourceValue, setSourceValue] = useState(value);
    const sourceValueLRef = useRefLive(value, setSourceValue);

    const [validation, setValidation] = useState<"maxLength">();
    const isInvalid = !!validation;

    useEffect(() => {
        validationHandler?.(isInvalid);
    }, [isInvalid, validationHandler]);

    useEffect(() => {
        if (sourceValue && isNumber(maxLength) && maxLength <= sourceValue.length) {
            setValidation("maxLength");
            return;
        }

        setValidation(undefined);

    }, [sourceValue, onChange, sourceValueLRef, maxLength]);

    const validationLabels = useInputValidationLabels();
    const validationMsg = useMemo(() => {
        switch (validation) {
            case "maxLength":
                return validationLabels.messageTooLong;
            default:
                return undefined;
        }
    }, [validationLabels, validation]);




    const editorEvents = useMemo<SimpleMDEReactProps['events']>(() => {
        return {
            beforeChange: (_, changedObj) => {
                // This is a workaround for the initial value when the editor is disabled.
                // We need to make sure that if the incoming value is exactly equals to the editor value, then we allow the editor to be updated.
                // Otherwise, the editor will not be able to digest the incoming value.
                let changedValue = changedObj.text.join("\n");
                if (changedValue === sourceValueLRef.current)
                    return;

                if (isDisabled) {
                    changedObj.cancel();
                    return;
                }
            },

        };
    }, [isDisabled, sourceValueLRef]);

    return (
        <div
            className={getClassNames([
                style.editorContainer,
                isDisabled ? style.disabled : '',
                className,
            ])}
            ref={ref}
        >
            {label && <InputLabel className={'df-field-label'} label={label} />}
            <SimpleMDE
                key={1}
                className={validationMsg ? style.invalid : undefined}
                value={sourceValue}
                placeholder={placeholder}
                onChange={onChange}
                options={customOptions}
                events={editorEvents}
            />
            {
                validationMsg && <Alert type='error'>{validationMsg}</Alert>
            }
        </div>
    );
});

export default withUIState(MarkdownEditor);
